import classNames from 'classnames'
import { Check, NavArrowDown } from 'iconoir-react'
import { useState } from 'react'
import Companies from 'app/components/Icon/CompaniesIcon'
import { CountBadge } from 'app/components/inbox/CountBadge'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'app/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import type { LocationJson } from 'app/types/entities'
import { useInboxLocations } from './useInboxLocations'

type Props = {
  location?: LocationJson
  unreadByLocation?: Record<string, number>
  disabled?: boolean
  compact?: boolean
  onChange: (location: LocationJson) => void
}

export function InboxLocationPicker({ location: selected, unreadByLocation, disabled, compact, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const { locations, loading } = useInboxLocations()

  // The conversation list already accounts for the open location, so counting it
  // here too would leave a number on the trigger that never clears as you work.
  // What the trigger is for is the inbox you cannot see from where you are.
  const unreadElsewhere = Object.entries(unreadByLocation ?? {}).reduce(
    (total, [id, count]) => (id === selected?.id ? total : total + count),
    0,
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipWrapper placement="right" message="Select location">
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            className={classNames(
              `flex h-8 items-center rounded-md border border-default bg-surface-raised text-left text-body-xs transition-colors hover:bg-surface-subtle disabled:cursor-not-allowed disabled:bg-surface-subtle`,
              compact ? `w-8 justify-center` : `w-full gap-x-2 px-2`,
            )}
          >
            <Companies width={16} height={16} className="shrink-0 text-subtle" />
            {!compact && (
              <>
                <span className={classNames(`min-w-0 flex-1 truncate`, !selected && `text-minimal`)}>
                  {selected?.name ?? (loading ? `Loading…` : `Please select a location`)}
                </span>
                {unreadElsewhere > 0 && (
                  <span aria-label={`${unreadElsewhere} unread in other locations`}>
                    <CountBadge value={unreadElsewhere} />
                  </span>
                )}
                <NavArrowDown className="h-3.5 w-3.5 shrink-0 text-subtle" strokeWidth={2} />
              </>
            )}
          </button>
        </PopoverTrigger>
      </TooltipWrapper>
      <PopoverContent
        align="start"
        sideOffset={4}
        className={classNames(`p-0 shadow-elevation-2`, compact ? `w-72` : `w-[var(--radix-popper-anchor-width)]`)}
      >
        <Command>
          <CommandInput placeholder="Search locations…" />
          <CommandList>
            <CommandEmpty>No locations found.</CommandEmpty>
            <CommandGroup>
              {locations.map((loc) => {
                const isSelected = loc.id === selected?.id
                const unread = unreadByLocation?.[loc.id!] ?? 0
                return (
                  <CommandItem
                    key={loc.id}
                    value={loc.name ?? loc.id!}
                    onSelect={() => {
                      onChange(loc)
                      setOpen(false)
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate">{loc.name}</span>
                    {unread > 0 && (
                      <span aria-label={`${unread} unread`}>
                        <CountBadge value={unread} className="ml-2" />
                      </span>
                    )}
                    {isSelected && <Check className="ml-2 h-4 w-4 text-default" strokeWidth={2} />}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
